/**
 * Organization Context
 *
 * Provides organization state and functions throughout the app.
 * - Tracks user's organization memberships
 * - Tracks currently selected organization
 * - Provides org switching functionality
 * - Persists selected org in localStorage
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
  Fragment,
} from 'react';
import { OrgMembership, OrgRole, SubscriptionTier } from '../api/auth';
import { useAuth } from './AuthContext';
import { getDisplayTimeZone, setDisplayTimeZone } from '../utils/dateFormatting';

// =============================================================================
// Constants
// =============================================================================

const CURRENT_ORG_KEY = 'currentOrgSlug';

// =============================================================================
// Types
// =============================================================================

interface OrganizationContextType {
  // State
  organizations: OrgMembership[];
  currentOrg: OrgMembership | null;
  isLoading: boolean;

  // Actions
  setCurrentOrg: (orgSlug: string) => void;
  switchOrganization: (orgSlug: string) => void;
  getOrgBySlug: (slug: string) => OrgMembership | undefined;

  // Utilities
  isOrgAdmin: boolean;
  isOrgOwner: boolean;
  hasMultipleOrgs: boolean;
  paymentTrackingEnabled: boolean;
  subscriptionTier: SubscriptionTier;
  isFreeTier: boolean;
  /** Server-computed: this org currently sends SMS (kill switch + tier). */
  smsEnabled: boolean;
}

// =============================================================================
// Context
// =============================================================================

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined
);

// =============================================================================
// Storage Helpers
// =============================================================================

function getStoredOrgSlug(): string | null {
  return localStorage.getItem(CURRENT_ORG_KEY);
}

function setStoredOrgSlug(slug: string): void {
  localStorage.setItem(CURRENT_ORG_KEY, slug);
}

/**
 * Pre-select which org becomes current on the NEXT orgs refresh — for flows
 * that add a membership outside this context (QR join): stamp the new slug
 * before refreshUser() and the init effect lands the user there instead of
 * restoring whatever org localStorage held from a previous session.
 */
export function rememberOrgSlugForNextLoad(slug: string): void {
  setStoredOrgSlug(slug);
}

function clearStoredOrgSlug(): void {
  localStorage.removeItem(CURRENT_ORG_KEY);
}

// =============================================================================
// Provider
// =============================================================================

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentOrg, setCurrentOrgState] = useState<OrgMembership | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get organizations from user
  const organizations = useMemo(() => user?.orgs || [], [user?.orgs]);

  /**
   * Get organization by slug
   */
  const getOrgBySlug = useCallback(
    (slug: string): OrgMembership | undefined => {
      return organizations.find((org) => org.orgSlug === slug);
    },
    [organizations]
  );

  /**
   * Set current organization
   */
  const setCurrentOrg = useCallback(
    (orgSlug: string) => {
      const org = getOrgBySlug(orgSlug);
      if (org) {
        setCurrentOrgState(org);
        setStoredOrgSlug(orgSlug);
      }
    },
    [getOrgBySlug]
  );

  /**
   * Switch organization (same as setCurrentOrg but can trigger side effects)
   */
  const switchOrganization = useCallback(
    (orgSlug: string) => {
      setCurrentOrg(orgSlug);
      // Could add additional side effects here like clearing caches
    },
    [setCurrentOrg]
  );

  /**
   * Initialize current org when user data is available
   */
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated || organizations.length === 0) {
      setCurrentOrgState(null);
      clearStoredOrgSlug();
      setIsLoading(false);
      return;
    }

    // Try to restore from localStorage
    const storedSlug = getStoredOrgSlug();
    if (storedSlug) {
      const storedOrg = getOrgBySlug(storedSlug);
      if (storedOrg) {
        setCurrentOrgState(storedOrg);
        setIsLoading(false);
        return;
      }
    }

    // Default to first organization
    const defaultOrg = organizations[0];
    if (defaultOrg) {
      setCurrentOrgState(defaultOrg);
      setStoredOrgSlug(defaultOrg.orgSlug);
    }

    setIsLoading(false);
  }, [authLoading, isAuthenticated, organizations, getOrgBySlug]);

  // Computed values
  const isOrgAdmin = currentOrg?.role === 'ADMIN' || currentOrg?.role === 'OWNER';
  const isOrgOwner = currentOrg?.role === 'OWNER';
  const hasMultipleOrgs = organizations.length > 1;
  const paymentTrackingEnabled = currentOrg?.paymentTrackingEnabled ?? false;
  const subscriptionTier: SubscriptionTier = currentOrg?.subscriptionTier ?? 'FREE';
  const isFreeTier = subscriptionTier === 'FREE';
  const smsEnabled = currentOrg?.smsEnabled ?? false;

  // Every date and time in the app is shown in the current org's zone, not
  // the device's (utils/dateFormatting.ts). Set it during render, before any
  // child formats a date in this pass.
  setDisplayTimeZone(currentOrg?.timezone);
  const displayTimeZone = getDisplayTimeZone();

  const value: OrganizationContextType = {
    organizations,
    currentOrg,
    isLoading,
    setCurrentOrg,
    switchOrganization,
    getOrgBySlug,
    isOrgAdmin,
    isOrgOwner,
    hasMultipleOrgs,
    paymentTrackingEnabled,
    subscriptionTier,
    isFreeTier,
    smsEnabled,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {/* Keyed on the zone: switching to an org in another timezone (or an
          owner changing it) remounts the tree, so no memoised component keeps
          dates formatted in the old zone. */}
      <Fragment key={displayTimeZone}>{children}</Fragment>
    </OrganizationContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext);

  if (context === undefined) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider'
    );
  }

  return context;
}

// =============================================================================
// Utility Hook - Get current org slug for API calls
// =============================================================================

/**
 * Returns the current org slug, useful for API calls
 * Returns null if no org is selected
 */
export function useCurrentOrgSlug(): string | null {
  const { currentOrg } = useOrganization();
  return currentOrg?.orgSlug || null;
}

/**
 * Returns the current org role
 */
export function useOrgRole(): OrgRole | null {
  const { currentOrg } = useOrganization();
  return currentOrg?.role || null;
}
